import React, { useMemo, useState } from "react";
import FullScreenModal from "../FullScreenModal/FullScreenModal";
import { IReducer } from "../../state/reducer";
import ActionSettingsRow from "../ActionSettingsRow/ActionSettingsRow";
import { ActionEntry } from "../../state/BrickActions/ActionEntries";
import "./ActionSettings.css";
import "../PrimaryFilter/PrimaryFilter.css";

interface ActionSettingsProps extends IReducer {
  isOpen: boolean;
  onClose: (arg0: boolean) => void;
}

const ActionSettings: React.FC<ActionSettingsProps> = ({
  state,
  dispatch,
  isOpen,
  onClose,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  // Get all unlocked actions (visible = true), including those hidden by user
  const unlockedActions = useMemo(() => {
    let actions: ActionEntry[] = [];

    state.actions.forEach((actionEntry) => {
      if (
        actionEntry &&
        actionEntry.visible &&
        actionEntry.isBrickpack === false &&
        actionEntry.type !== "BuySet" // Exclude BuySet actions (they're in Sets module)
      ) {
        actions.push(actionEntry);
      }
    });

    return actions;
  }, [state.actions]);

  // Filter actions based on search term
  const filteredActions = useMemo(() => {
    if (!searchTerm.trim()) {
      return unlockedActions;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return unlockedActions.filter(
      (action) =>
        action.title.toLowerCase().includes(lowerSearchTerm) ||
        action.type.toLowerCase().includes(lowerSearchTerm) ||
        action.id.toLowerCase().includes(lowerSearchTerm),
    );
  }, [unlockedActions, searchTerm]);

  return (
    <FullScreenModal
      header={"Action Settings"}
      isOpen={isOpen}
      onClose={() => onClose(false)}
      helpURL="https://www.google.com"
    >
      <div className="actionSettingsContent">
        <div className="actionSettingsSection">
          <div className="actionSettingsSectionTitle">Reset Action Order</div>
          <div className="actionSettingsSectionDescription">
            Reset the custom action order to the default order. This cannot be
            undone.
          </div>
          {!showResetConfirmation ? (
            <button
              className="resetOrderButton"
              onClick={() => setShowResetConfirmation(true)}
            >
              Reset Action Order
            </button>
          ) : (
            <div className="resetConfirmation">
              <div className="resetConfirmationText">
                Are you sure you want to reset the action order to default? This
                cannot be undone!
              </div>
              <div className="resetConfirmationButtons">
                <button
                  className="resetOrderButton"
                  onClick={() => {
                    dispatch({ type: "SET_ACTION_ORDER", actionOrder: [] });
                    setShowResetConfirmation(false);
                  }}
                >
                  Yes, Reset Order
                </button>
                <button
                  className="resetOrderCancelButton"
                  onClick={() => setShowResetConfirmation(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="actionSettingsSection">
          <div className="actionSettingsSectionTitle">
            Toggle Action Visibility
          </div>
          <div className="actionSettingsSectionDescription">
            Hide or show unlocked actions in the Actions module. Hidden actions
            won't appear in the main view but remain unlocked.
          </div>
          <div className="actionSettingsSearchContainer">
            <input
              type="text"
              className="searchInput"
              placeholder="Search actions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="actionSettingsRows">
            {unlockedActions.length > 0 ? (
              filteredActions.length > 0 ? (
                filteredActions.map((action) => (
                  <ActionSettingsRow
                    key={action.id}
                    id={action.id}
                    title={action.title}
                    type={action.type}
                    userHidden={action.userHidden}
                    state={state}
                    dispatch={dispatch}
                  />
                ))
              ) : (
                <div className="noUnlockedActionsMessage">
                  No actions match your search "{searchTerm}".
                </div>
              )
            ) : (
              <div className="noUnlockedActionsMessage">
                No unlocked actions available yet. Keep playing to unlock more!
              </div>
            )}
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
};
export default ActionSettings;
